import React from 'react';
import { Bell, BellOff, Volume2, VolumeX } from 'lucide-react';
import type { CaptainMessageType } from '../types/events';
import { getMessageTypeLabel } from '../core/OrderMessageResolver';
import { getCaptainLogoUrl } from '../../constants/images';

interface CaptainAvatarProps {
  size?: number;
  ring?: boolean;
  pulse?: boolean;
}

export const CaptainAvatar: React.FC<CaptainAvatarProps> = ({
  size = 40,
  pulse = false,
}) => (
  <div
    className={`vc-avatar${pulse ? ' vc-avatar--pulse' : ''}`}
    style={{ width: size, height: size }}
  >
    <img
      src={getCaptainLogoUrl()}
      alt=""
      className="vc-avatar__img"
      width={size}
      height={size}
      aria-hidden
    />
  </div>
);

export const CaptainSoundwave: React.FC<{ active?: boolean }> = ({ active = false }) => (
  <svg width="20" height="14" viewBox="0 0 20 14" fill="none" className="vc-soundwave" aria-hidden>
    {[0, 1, 2, 3, 4].map((i) => (
      <rect
        key={i}
        x={i * 4}
        y={active ? 0 : 3}
        width="2.5"
        rx="1.25"
        height={active ? 14 : 8}
        className={active ? 'vc-soundwave__bar vc-soundwave__bar--active' : 'vc-soundwave__bar'}
        style={{ animationDelay: active ? `${i * 0.1}s` : undefined }}
      />
    ))}
  </svg>
);

export const CaptainVoiceButton: React.FC<{
  on: boolean;
  onToggle: () => void;
}> = ({ on, onToggle }) => (
  <button
    type="button"
    className={`vc-popover__icon-btn vc-popover__icon-btn--round${on ? ' vc-popover__icon-btn--active' : ''}`}
    onClick={onToggle}
    title={on ? 'Turn voice off' : 'Turn voice on'}
    aria-label={on ? 'Voice on. Tap to mute.' : 'Voice off. Tap to enable spoken updates.'}
  >
    {on ? <Volume2 size={18} strokeWidth={2.2} aria-hidden /> : <VolumeX size={18} strokeWidth={2.2} aria-hidden />}
  </button>
);

export const CaptainUpdatesSwitch: React.FC<{
  on: boolean;
  onToggle: () => void;
  compact?: boolean;
}> = ({ on, onToggle, compact = false }) => (
  <button
    type="button"
    className={`vc-updates-switch${on ? ' vc-updates-switch--on' : ''}${compact ? ' vc-updates-switch--compact' : ''}`}
    role="switch"
    aria-checked={on}
    onClick={onToggle}
    title={on ? 'Turn off automatic updates' : 'Turn on automatic updates'}
    aria-label={on ? 'Automatic updates on. Tap to turn off.' : 'Automatic updates off. Tap to turn on.'}
  >
    {on ? <Bell size={15} strokeWidth={2.2} aria-hidden /> : <BellOff size={15} strokeWidth={2.2} aria-hidden />}
    {!compact && <span className="vc-updates-switch__label">{on ? 'Updates on' : 'Updates off'}</span>}
    <span className="vc-updates-switch__track" aria-hidden>
      <span className="vc-updates-switch__knob" />
    </span>
  </button>
);

/** Open full chat panel — corners expand outward. */
export const CaptainExpandIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M15 3h6v6" />
    <path d="M9 21H3v-6" />
    <path d="M21 3l-7 7" />
    <path d="M3 21l7-7" />
  </svg>
);

/** Close expanded panel. */
export const CaptainCloseIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    aria-hidden
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

/** Chat bubble for Start Chat CTA. */
export const CaptainChatIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const TYPE_ICONS: Record<CaptainMessageType, string> = {
  welcome: '👋',
  placed: '✅',
  confirmed: '✅',
  preparing: '🍳',
  ready: '🛎️',
  complete: '🙏',
  cancelled: '❌',
  wait_time: '⏱️',
  table_ready: '🪑',
  nudge: '💡',
  item_status: '🍽️',
  assistance: '🔔',
  system: 'ℹ️',
};

export function getTypeIcon(type: CaptainMessageType): string {
  return TYPE_ICONS[type] || 'ℹ️';
}

export function getTypeAccentClass(type: CaptainMessageType): string {
  switch (type) {
    case 'ready':
      return 'vc-accent--success';
    case 'cancelled':
      return 'vc-accent--error';
    case 'preparing':
    case 'wait_time':
      return 'vc-accent--orange';
    case 'complete':
      return 'vc-accent--purple';
    default:
      return 'vc-accent--gold';
  }
}

interface CaptainMessageCardProps {
  type: CaptainMessageType;
  title?: string;
  text: string;
  time: string;
  eta?: string;
  animate?: boolean;
}

export const CaptainMessageCard: React.FC<CaptainMessageCardProps> = ({
  type,
  title,
  text,
  time,
  eta,
  animate,
}) => {
  const label = title || getMessageTypeLabel(type);
  return (
    <div className={`vc-message-card${animate ? ' vc-message-card--animate' : ''}`}>
      <div className={`vc-message-card__accent ${getTypeAccentClass(type)}`} />
      <div className="vc-message-card__body">
        <div className="vc-message-card__header">
          <span className="vc-message-card__icon">{getTypeIcon(type)}</span>
          <span className={`vc-message-card__label ${getTypeAccentClass(type)}`}>{label}</span>
          {eta && <span className="vc-message-card__eta">{eta}</span>}
        </div>
        <p className="vc-message-card__text">{text}</p>
        <div className="vc-message-card__time">{time}</div>
      </div>
    </div>
  );
};
